import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { msalInstance } from '../../services/msalAuthService'
import axios from 'axios'
import { loginRequest, apiConfig } from '../../config/authConfig'

const user = JSON.parse(localStorage.getItem('user'))

const initialState = {
  user: user || null,
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: '',
  // Set true the moment a logout is initiated; consumed by the App-level
  // overlay so we don't briefly render /login (or any other route) between
  // dispatching logout and the browser navigating away to AAD.
  isLoggingOut: false,
}

// Login user with MSAL (redirect flow)
// loginRedirect navigates the page away to AAD; the returned Promise resolves
// only on failure. Successful sign-in is completed by handleRedirectPromise()
// in main.jsx after AAD redirects back.
export const login = createAsyncThunk('auth/login', async (_, thunkAPI) => {
  try {
    await msalInstance.loginRedirect(loginRequest)
    return null
  } catch (error) {
    const message = error.message || error.toString()
    return thunkAPI.rejectWithValue(message)
  }
})

// Logout user with MSAL (redirect flow)
// Security Finding 8.6 - Properly terminate session.
// IMPORTANT: We do NOT clear Redux user here. Doing so before logoutRedirect
// causes PrivateRoute to re-render and briefly show /login before the browser
// navigates to AAD. Instead we set isLoggingOut (handled by caller / overlay),
// wipe storage, then trigger logoutRedirect. Final Redux/state cleanup runs
// on the post-logout landing page (/logout) once AAD redirects back.
export const logout = createAsyncThunk('auth/logout', async () => {
  // Security Finding 8.6 - Call backend LogOutUser FIRST so the server-side
  // session in TokenSession.sessionList is removed. Without this, the encrypted
  // access_token would continue to authenticate API calls (e.g. from Postman
  // or any cached client) until the session naturally expired.
  try {
    const accessToken = localStorage.getItem('access_token')
    if (accessToken) {
      await axios.get(`${apiConfig.uri}/Login/LogOutUser`, {
        headers: {
          access_token: accessToken,
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
        withCredentials: false,
        timeout: 5000,
      })
    }
  } catch (e) {
    // Best-effort: even if the server call fails, continue with local + MSAL
    // cleanup so the user is not stuck on the page.
    // eslint-disable-next-line no-console
    console.warn('Backend LogOutUser failed (continuing with local logout):', e?.message || e)
  }

  try {
    localStorage.clear()
    sessionStorage.clear()
  } catch (_) {
    /* no-op */
  }

  try {
    await msalInstance.logoutRedirect()
  } catch (e) {
    // Best-effort fallback if redirect cannot be initiated
    try {
      await msalInstance.clearCache?.()
    } catch (_) {
      /* no-op */
    }
  }

  return true
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false
      state.isError = false
      state.isSuccess = false
      state.message = ''
    },
    setUser: (state, action) => {
      state.user = action.payload
      state.isSuccess = true
      localStorage.setItem('user', JSON.stringify(action.payload))
    },
    clearUser: (state) => {
      state.user = null
      state.isSuccess = false
      localStorage.removeItem('user')
    },
    setLoggingOut: (state, action) => {
      state.isLoggingOut = action.payload === undefined ? true : !!action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.user = action.payload
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
        state.user = null
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null
      })
  },
})

export const { reset, setUser, clearUser, setLoggingOut } = authSlice.actions
export default authSlice.reducer
